import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react"
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue,
} from "motion/react"

import { cn } from "../../../lib/utils"

export const PANEL_DOCK_RADIUS_PX = 150
export const PANEL_DOCK_SHIFT_PX = 16
export const PANEL_DOCK_SPRING = { mass: 0.1, stiffness: 150, damping: 12 } as const

type DockPointer = {
  y: MotionValue<number>
  epoch: MotionValue<number>
}

const PanelDockContext = createContext<DockPointer | null>(null)

/**
 * Piecewise-linear tent: peak leftward shift at distance 0, zero at and beyond
 * the radius. Clamps Infinity / NaN to 0 so a "pointer away" sentinel is safe.
 */
export function dockShiftPx(
  distance: number,
  radius = PANEL_DOCK_RADIUS_PX,
  maxShift = PANEL_DOCK_SHIFT_PX,
): number {
  if (!Number.isFinite(distance) || radius <= 0) return 0
  const abs = Math.abs(distance)
  if (abs >= radius) return 0
  return -maxShift * (1 - abs / radius)
}

function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el?.parentElement ?? null
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      return node
    }
    node = node.parentElement
  }
  return null
}

export function PanelDock({
  className,
  children,
  style,
}: {
  className?: string
  children: ReactNode
  style?: CSSProperties
}) {
  const pointerY = useMotionValue(Infinity)
  const layoutEpoch = useMotionValue(0)
  const lastClientY = useRef(Infinity)
  const rootRef = useRef<HTMLDivElement>(null)
  const dockPointer = useMemo(
    () => ({ y: pointerY, epoch: layoutEpoch }),
    [pointerY, layoutEpoch],
  )

  useEffect(() => {
    const scrollParent = findScrollParent(rootRef.current)
    if (!scrollParent) return

    const bumpFromLastY = () => {
      if (!Number.isFinite(lastClientY.current)) return
      // Epoch must change: MotionValue.set(same number) does not notify, and
      // cards have moved under a stationary pointer after scroll.
      layoutEpoch.set(layoutEpoch.get() + 1)
    }

    scrollParent.addEventListener("scroll", bumpFromLastY, { passive: true })
    return () => scrollParent.removeEventListener("scroll", bumpFromLastY)
  }, [layoutEpoch])

  return (
    <PanelDockContext.Provider value={dockPointer}>
      <div
        ref={rootRef}
        className={cn(className)}
        style={style}
        onMouseMove={(event) => {
          lastClientY.current = event.clientY
          pointerY.set(event.clientY)
        }}
        onMouseLeave={() => {
          lastClientY.current = Infinity
          pointerY.set(Infinity)
        }}
      >
        {children}
      </div>
    </PanelDockContext.Provider>
  )
}

const ZERO_PX = "0px"

/**
 * Spring-smoothed CSS length for `--panel-dock-x`. Always calls the same hooks
 * so order stays stable when a card renders outside a PanelDock (Storybook,
 * non-inline portal). Reduced motion and missing provider both yield `"0px"`.
 */
export function usePanelDockShift(ref: RefObject<HTMLElement | null>): MotionValue<string> {
  const pointer = useContext(PanelDockContext)
  const idleY = useMotionValue(0)
  const idleEpoch = useMotionValue(0)
  const prefersReduced = useReducedMotion()
  const sourceY = pointer?.y ?? idleY
  const sourceEpoch = pointer?.epoch ?? idleEpoch

  const shift = useTransform([sourceY, sourceEpoch], ([rawY]) => {
    if (prefersReduced || !pointer) return 0
    const y = typeof rawY === "number" ? rawY : Number.NaN
    const el = ref.current
    if (!el || !Number.isFinite(y)) return 0
    const bounds = el.getBoundingClientRect()
    return dockShiftPx(y - (bounds.top + bounds.height / 2))
  })

  const spring = useSpring(shift, PANEL_DOCK_SPRING)
  return useTransform(spring, (v) => (prefersReduced || !pointer ? ZERO_PX : `${v}px`))
}

export { motion }
